import { useDateHelpers } from '@/lib/helpers/date';
import classnames from 'classnames';
import { useDarkModeContext } from '../../../DarkModeContext/useDarkModeContext';
import { useMemo } from 'react';

interface VariantProps {
  selected?: boolean;
  location?: string;
  startDate?: string;
  endDate?: string;
  isLive?: boolean;
  timeZone?: string;
  subLabel?: string;
  onSelect?: () => void;
  isOnline?: boolean;
  customDuration?: string;
  ecomBoxSessions?: number;
}

const Variant: React.FC<VariantProps> = ({
  location,
  startDate,
  endDate,
  isLive,
  subLabel,
  timeZone,
  selected,
  onSelect,
  customDuration,
  ecomBoxSessions,
  isOnline
}) => {
  const darkmodeContext = useDarkModeContext();
  const { getFormattedStartDateWithSessions } = useDateHelpers();
  const isDarkMode = darkmodeContext?.isDarkMode ?? false;

  const boxClasses = classnames(
    'w-full rounded border px-6 py-4 text-left transition-colors hover:border-black lg:px-5',
    { 'border-silver': !selected && !isDarkMode },
    { 'border-gray-medium': !selected && isDarkMode },
    { 'border-black bg-gray-lightest': selected && !isDarkMode },
    { 'border-white bg-gray-dark': selected && isDarkMode }
  );

  const textColorClass = isDarkMode ? 'text-white' : '';

  const dateRange = useMemo(
    () =>
      !!startDate &&
      timeZone &&
      getFormattedStartDateWithSessions(
        timeZone,
        startDate,
        endDate,
        customDuration,
        ecomBoxSessions
      ),
    [
      endDate,
      getFormattedStartDateWithSessions,
      startDate,
      timeZone,
      customDuration,
      ecomBoxSessions
    ]
  );

  return (
    <button className={boxClasses} onClick={onSelect} data-component="Variant">
      <div
        className={classnames(
          { textColorClass, '!items-start': isOnline },
          'flex items-center justify-between'
        )}
      >
        {isOnline ? (
          <div className="flex flex-col">
            <span className={classnames(textColorClass, 'text-2 font-medium')}>Online</span>
            <span className={classnames(textColorClass, 'label mt-[8px] block opacity-60')}>
              {location}
            </span>
          </div>
        ) : (
          <span className={classnames(textColorClass, 'text-2 font-medium')}>{location}</span>
        )}
        {isLive ? (
          <div
            className={classnames(textColorClass, 'label flex items-center gap-2 text-gray-dark')}
          >
            <span className="block size-3 rounded-full bg-red"></span>
            Live
          </div>
        ) : (
          <p
            style={{ lineHeight: isOnline ? '1.25rem' : undefined }}
            className={classnames(textColorClass, 'label text-gray-dark')}
          >
            {dateRange}
          </p>
        )}
      </div>
      {subLabel && (
        <p className={classnames(textColorClass, 'label mt-[8px] block opacity-60')}>{subLabel}</p>
      )}
    </button>
  );
};

export default Variant;
